import { useState, useContext, useEffect, useCallback } from 'react';
import { Link, useRouteLoaderData } from 'react-router-dom';
import { ModalContext } from '../context/ModalProvider';
import { useGlobalState } from "../configurator/State";
import bmkservice from '../service/bmk-service';
import OptimizedImg from '../components/OptimizedImg';

export default function Product({ product }) {
    const [showPackageSelector, setPackageSelector] = useState(false);
    const config = useRouteLoaderData("root");

    const [minPrice, setMinPrice] = useState(config.calcPrices?.includes(product.ParentGroupId) ? null : product.MinMSRP);
    const [calculatedPrices, setCalculatedPrices] = useState({});
    const hp = product.HP ? product.HP : null;

    const calcPrices = useCallback(async () => {
        const cacheKey = `bmk-prices-${product.ParentGroupId}`;
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                const { prices, minPrice: cachedMin } = JSON.parse(cached);
                setCalculatedPrices(prices);
                setMinPrice(cachedMin);
                return;
            }
        } catch (e) { /* sessionStorage unavailable — proceed with fetch */ }

        const prices = {};
        const msrps = [];

        await Promise.all(product.BMKGroups.map(async bmkGroup => {
            await Promise.all(bmkGroup.ProductList.map(async tractor => {
                const productResponse = await fetch(bmkservice.getProductDetails + tractor.Name.trim());
                const productJson = await productResponse.json();

                let addOnMsrp = productJson.Tractor.DefaultTires.MsrpPrice;
                productJson.Tractor.IncludedItems.forEach(item => {
                    addOnMsrp += item.MsrpPrice;
                });

                const calculatedPrice = productJson.Tractor.MSRPWithoutTires + addOnMsrp;
                prices[tractor.Id] = calculatedPrice;
                msrps.push(calculatedPrice);
            }));
        }));

        setCalculatedPrices(prices);
        const computedMin = Math.min(...msrps);
        setMinPrice(computedMin);

        try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ prices, minPrice: computedMin }));
        } catch (e) { /* quota exceeded — prices still display, just won't cache */ }
    }, [product.BMKGroups, product.ParentGroupId]);

    useEffect(() => {
        if (config.calcPrices?.includes(product.ParentGroupId)) {
            calcPrices();
        }
    }, [calcPrices, config.calcPrices, product.ParentGroupId]);


    const searchRegExp = /[\/]/g;
    const replaceWith = ' / ';

    return (
        <div className="modelContainer eligible" onClick={() => !showPackageSelector && setPackageSelector(true)}>
            <span className="groupThumbContainer">
                <OptimizedImg src={product.ImageUrl} width={600} className="groupThumb thumb" alt={product.Name} loading="lazy" />
            </span>
            <span className="modelHeadline">Model</span>
            <span className="modelNameHeadline">{product.Name.replace(searchRegExp, replaceWith)}</span>
            <span className="modelDescription">{product.Description}</span>
            <span className="modelHPMsrpContainer">
                {hp &&
                    <span className="hp-container centerIfOnly">
                        <span className="hp-value">{hp}</span>
                        <span className="hp-label">HP</span>
                    </span>
                }
                {minPrice &&
                    <span className="msrp-container centerIfOnly">
                        <span className="msrp-value">{minPrice ? '$' + minPrice.toLocaleString() : ''}</span>
                        <span className="msrp-label">MSRP As Low As</span>
                    </span>
                }
            </span>
            {
                (product.ParentGroupId === 124 && !minPrice) ? (
                    <button className="button orange-button choosePackage eligible" disabled>Loading...</button>
                ) : (
                    <button className="button orange-button choosePackage eligible" onClick={() => setPackageSelector(true)}>Choose a Package</button>
                )
            }
            {showPackageSelector &&
                <PackageList product={product} calculatedPrices={calculatedPrices} closeHandler={() => (setPackageSelector(false))} />
            }
        </div>
    );
}


function PackageList({ product, calculatedPrices, closeHandler }) {
    const modal = useContext(ModalContext);
    const [navMode] = useGlobalState("NavigationMode");
    const defaultPackage = product.BMKGroups?.[0]?.ProductList?.[0]?.Name || product.ProductList?.[0]?.Name;
    const defaultGroup = product.BMKGroups?.[0]?.Name || (product.BMKGroups?.[0] ? "Package Group" : undefined);
    const [selectedPackage, setPackage] = useState(defaultPackage);
    const [openGroups, setOpenGroups] = useState(defaultGroup ? [defaultGroup] : []);

    function toggleOpenGroup(name) {
        if (openGroups.includes(name)) {
            setOpenGroups(openGroups.filter(i => i !== name));
        }
        else {
            setOpenGroups([...openGroups, name]);
        }
    }

    const sendEvent = () => {
        modal.show();
        window.gtag('event', 'Model Selected', {
            'Series': product.Name
        });
    }

    return (
        <div className="modelPicker">
            <button className="unPick" onClick={closeHandler}><em className="bi bi-x"></em></button>
            <div className="modelPickerHeader">Base Model Picker</div>
            <div className="modelPickerDisclaimer">Please select a package to get started</div>
            <div className="modelPickerItemsContainer">
                {product.BMKGroups?.length > 0 ? (
                    <>
                        {product.BMKGroups.map(packageGroup => {
                            let groupName = packageGroup.Name ? packageGroup.Name : "Package Group";
                            return (
                                <div key={packageGroup.Id} className={"modelPickerGroup" + (openGroups.includes(groupName) ? " open" : "")}>
                                    <h2 onClick={() => toggleOpenGroup(groupName)}>{groupName}<em className={openGroups.includes(groupName) ? "bi bi-chevron-up" : "bi bi-chevron-down"}></em></h2>
                                    {openGroups.includes(groupName) && packageGroup.ProductList?.map(productPackage => (
                                        <ProductPackage key={productPackage.Id}
                                            productPackage={productPackage}
                                            calculatedPrices={calculatedPrices}
                                            active={productPackage.Name === selectedPackage}
                                            clickHandler={() => setPackage(productPackage.Name)}
                                        />
                                    ))}
                                </div>
                            )
                        })}
                        {product.ProductList?.length > 0 && (
                            <div className={"modelPickerGroup" + (openGroups.includes("other") ? " open" : "")}>
                                <h2 onClick={() => toggleOpenGroup("other")}>Other<em className={openGroups.includes("other") ? "bi bi-chevron-up" : "bi bi-chevron-down"}></em></h2>
                                {openGroups.includes("other") && product.ProductList.map(productPackage => (
                                    <ProductPackage key={productPackage.Id}
                                        productPackage={productPackage}
                                        calculatedPrices={calculatedPrices}
                                        active={productPackage.Name === selectedPackage}
                                        clickHandler={() => setPackage(productPackage.Name)}
                                    />
                                ))}
                            </div>
                        )}
                    </>

                ) : (
                    product.ProductList.map(productPackage => (
                        <ProductPackage key={productPackage.Id}
                            productPackage={productPackage}
                            calculatedPrices={calculatedPrices}
                            active={productPackage.Name === selectedPackage}
                            clickHandler={() => setPackage(productPackage.Name)}
                        />
                    ))
                )}

            </div>
            {selectedPackage ? (
                <Link to={encodeURIComponent(selectedPackage.trim()) + (navMode === "landpride" ? "/land-pride/" : "")} relative="path" onClick={sendEvent} className="startConfiguring orange button orange-button eligible">Start Configuring</Link>

            ) : (
                <div className="startConfiguring orange button orange-button eligible disabled">Start Configuring</div>
            )}
        </div>
    );
}


function ProductPackage({ productPackage, calculatedPrices, active, clickHandler }) {
    const finalPrice = calculatedPrices[productPackage.Id] || productPackage.MsrpPrice;
    const displayPrice = finalPrice ? '$' + finalPrice.toLocaleString() : 'Call for Pricing';
    const packageDetails = productPackage.Description ? productPackage.Description : productPackage.Name;

    return (
        <div className="modelPickerItem">
            <button className="modelPickerRadioContainer chooseCurrentModel" onClick={clickHandler}>
                <em className={active ? "bi bi-check-circle-fill orange" : "bi bi-circle"}></em>
            </button>
            <div className="modelPickerContentContainer">
                <span className="modelPickerContentDescription">{packageDetails}</span>
                <span className="modelPickerContentCost">{displayPrice}</span>
            </div>
        </div>
    );
}
